import React, { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  subscribeOngoingProjects,
  createExpense,
  updateExpense,
  adminUpdateExpense,
  deleteExpense,
  bulkDeleteExpenses,
  approveExpense,
  getProjectBalance,
  isExpenseVisibleToAdmin,
  formatEditTooltip,
} from '../../services/expenseFirestore';
import { EXPENSE_STATUSES } from '../../constants/expenseConstants';
import { useAuth } from '../../contexts/AuthContext';
import SearchableDropdown from '../ui/SearchableDropdown';
import Modal from '../ui/Modal';
import ConfirmModal from '../ui/ConfirmModal';
import Badge from '../ui/Badge';
import './ExpenseDashboard.css';

const EMPTY = {
  projectDocId: '', projectId: '', projectName: '', projectStatus: '',
  date: new Date().toISOString().split('T')[0], expense: '', amount: '',
  status: 'Paid', serviceProvidedPersonName: '',
};

function formatCurrency(n) {
  return `₹ ${Number(n || 0).toLocaleString('en-IN')}`;
}

function isPendingReview(expense) {
  return expense.sentToAdmin !== false && expense.approved === false;
}

export default function ExpenseDashboard({ expenses, projects, loading, isAdmin = true }) {
  const { currentUser, userProfile } = useAuth();
  const [ongoingProjects, setOngoingProjects] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedProjects, setExpandedProjects] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());

  React.useEffect(() => {
    const unsub = subscribeOngoingProjects(setOngoingProjects);
    return unsub;
  }, []);

  const projectOptions = useMemo(() =>
    ongoingProjects.map((p) => ({ value: p.id, label: `${p.projectId || '—'} — ${p.projectName}`, project: p })),
  [ongoingProjects]);

  const selectedProject = ongoingProjects.find((p) => p.id === form.projectDocId)
    || projects.find((p) => p.id === form.projectDocId);
  const balance = selectedProject ? getProjectBalance(selectedProject, expenses) : null;
  const noActive = ongoingProjects.length === 0;

  const adminExpenses = useMemo(
    () => expenses.filter(isExpenseVisibleToAdmin),
    [expenses],
  );

  const filteredExpenses = useMemo(() => {
    if (!search.trim()) return adminExpenses;
    const q = search.toLowerCase();
    return adminExpenses.filter((e) =>
      ['projectId', 'projectName', 'expense', 'employeeName'].some(
        (k) => String(e[k] ?? '').toLowerCase().includes(q),
      ),
    );
  }, [adminExpenses, search]);

  const groupedByProject = useMemo(() => {
    const map = new Map();
    filteredExpenses.forEach((exp) => {
      const key = exp.projectDocId || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          projectDocId: key,
          projectName: exp.projectName || 'Unknown Project',
          projectId: exp.projectId || '—',
          expenses: [],
          total: 0,
        });
      }
      const group = map.get(key);
      group.expenses.push(exp);
      group.total += Number(exp.amount) || 0;
      if (isPendingReview(exp)) {
        group.pendingCount = (group.pendingCount || 0) + 1;
      }
    });
    return Array.from(map.values()).sort((a, b) => a.projectName.localeCompare(b.projectName));
  }, [filteredExpenses]);

  const allVisibleIds = useMemo(
    () => filteredExpenses.map((e) => e.id),
    [filteredExpenses],
  );

  const toggleProject = useCallback((projectDocId) => {
    setExpandedProjects((prev) => ({ ...prev, [projectDocId]: !prev[projectDocId] }));
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === allVisibleIds.length) return new Set();
      return new Set(allVisibleIds);
    });
  }, [allVisibleIds]);

  const handleProjectSelect = (docId, opt) => {
    const p = opt?.project || ongoingProjects.find((x) => x.id === docId);
    if (!p) return;
    setForm((f) => ({
      ...f,
      projectDocId: p.id,
      projectId: p.projectId || '',
      projectName: p.projectName || '',
      projectStatus: p.status || '',
    }));
  };

  const validate = () => {
    if (!form.projectDocId) { toast.warning('Select a project'); return false; }
    if (!form.expense?.trim()) { toast.warning('Expense description required'); return false; }
    if (!form.amount || Number(form.amount) <= 0) { toast.warning('Valid amount required'); return false; }
    if (form.status === 'Unpaid' && (form.serviceProvidedPersonName?.trim() || '').length < 3) {
      toast.warning('Service provider name must be at least 3 characters'); return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        projectDocId: form.projectDocId,
        projectId: form.projectId,
        projectName: form.projectName,
        expense: form.expense,
        amount: form.amount,
        date: form.date,
        status: form.status,
        serviceProvidedPersonName: form.status === 'Unpaid' ? form.serviceProvidedPersonName : '',
      };

      if (editModal) {
        if (isAdmin) {
          await adminUpdateExpense(editModal.id, payload, {
            name: userProfile?.name,
            email: currentUser?.email,
          });
        } else {
          await updateExpense(editModal.id, { ...payload, approved: isAdmin, sentToAdmin: true });
        }
        toast.success('Expense updated');
        setEditModal(null);
      } else {
        await createExpense({
          ...payload,
          approved: isAdmin,
          sentToAdmin: true,
          employeeName: 'Admin',
        }, currentUser?.uid);
        toast.success('Expense added');
        setShowForm(false);
      }
      setForm(EMPTY);
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (exp) => {
    try {
      await approveExpense(exp.id);
      toast.success('Expense approved');
    } catch (e) {
      toast.error(e.message || 'Failed to approve');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteExpense(deleteTarget.id);
      toast.success('Expense deleted');
      setDeleteTarget(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await bulkDeleteExpenses(Array.from(selectedIds));
      toast.success(`${selectedIds.size} expense(s) deleted`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
    } catch (e) {
      toast.error(e.message || 'Failed to delete expenses');
    }
  };

  const openEdit = (exp) => {
    setForm({
      projectDocId: exp.projectDocId,
      projectId: exp.projectId,
      projectName: exp.projectName,
      projectStatus: '',
      date: exp.date || '',
      expense: exp.expense || '',
      amount: exp.amount || '',
      status: exp.status || 'Paid',
      serviceProvidedPersonName: exp.serviceProvidedPersonName || '',
    });
    setEditModal(exp);
  };

  const formFields = (
    <>
      {noActive && !editModal && <div className="emp-alert">No active projects available</div>}
      <div className="ui-form-row">
        <div className="ui-form-group">
          <label>Project Name *</label>
          <SearchableDropdown
            options={projectOptions}
            value={form.projectDocId}
            onChange={handleProjectSelect}
            placeholder="Search project…"
            disabled={noActive && !editModal}
            emptyMessage="No active projects available"
          />
        </div>
        <div className="ui-form-group"><label>Project ID</label><input readOnly value={form.projectId} /></div>
      </div>
      <div className="ui-form-row">
        <div className="ui-form-group"><label>Project Status</label><input readOnly value={form.projectStatus} /></div>
        {isAdmin && selectedProject && (
          <>
            <div className="ui-form-group">
              <label>Project Value</label>
              <input readOnly value={formatCurrency(selectedProject.projectValue)} />
            </div>
            <div className="ui-form-group">
              <label>Remaining Balance</label>
              <input readOnly value={formatCurrency(balance)} />
            </div>
          </>
        )}
      </div>
      <div className="ui-form-row">
        <div className="ui-form-group">
          <label>Date *</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div className="ui-form-group">
          <label>Amount (₹) *</label>
          <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </div>
      </div>
      <div className="ui-form-group">
        <label>Expense *</label>
        <input value={form.expense} onChange={(e) => setForm({ ...form, expense: e.target.value })} />
      </div>
      <div className="ui-form-group">
        <label>Status</label>
        <select
          value={form.status}
          onChange={(e) => setForm({
            ...form,
            status: e.target.value,
            serviceProvidedPersonName: e.target.value === 'Paid' ? '' : form.serviceProvidedPersonName,
          })}
        >
          {EXPENSE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {form.status === 'Unpaid' && (
        <div className="ui-form-group ui-field-animate">
          <label>Service Provided Person Name *</label>
          <input
            value={form.serviceProvidedPersonName}
            onChange={(e) => setForm({ ...form, serviceProvidedPersonName: e.target.value })}
            placeholder="Enter the Service Provided Person Name"
            minLength={3}
            required
          />
        </div>
      )}
    </>
  );

  return (
    <div className="expense-panel exp-dashboard-grouped">
      <div className="expense-card-header">
        <h3 className="expense-panel-title">Expense Management</h3>
        <div className="exp-dash-actions">
          {selectedIds.size > 0 && (
            <button type="button" className="ui-btn ui-btn-danger" onClick={() => setBulkDeleteOpen(true)}>
              <i className="fas fa-trash" /> Delete ({selectedIds.size})
            </button>
          )}
          <button type="button" className="btn-primary" onClick={() => { setForm(EMPTY); setShowForm(true); }} disabled={noActive}>
            <i className="fas fa-plus" /> Add Expense
          </button>
        </div>
      </div>

      <div className="exp-group-toolbar">
        <input
          type="search"
          className="ui-data-table-search"
          placeholder="Search expenses…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="exp-select-all">
          <input
            type="checkbox"
            checked={allVisibleIds.length > 0 && selectedIds.size === allVisibleIds.length}
            onChange={toggleSelectAll}
          />
          Select All
        </label>
        <span className="ui-data-table-count">{filteredExpenses.length} expenses · {groupedByProject.length} projects</span>
      </div>

      {loading ? (
        <p className="expense-loading">Loading expenses…</p>
      ) : groupedByProject.length === 0 ? (
        <p className="expense-loading">No expenses found</p>
      ) : (
        <div className="exp-project-groups">
          {groupedByProject.map((group) => {
            const isExpanded = expandedProjects[group.projectDocId] === true;
            const hasNew = (group.pendingCount || 0) > 0;
            return (
              <div
                key={group.projectDocId}
                className={`exp-project-group${hasNew ? ' exp-project-group--has-new' : ''}`}
              >
                <button
                  type="button"
                  className="exp-project-group-header"
                  onClick={() => toggleProject(group.projectDocId)}
                  aria-expanded={isExpanded}
                >
                  {hasNew && <span className="exp-group-new-dot" aria-label="New expenses" />}
                  <span className="exp-group-arrow">
                    <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                  </span>
                  <div className="exp-group-info">
                    <strong>{group.projectId} — {group.projectName}</strong>
                    <span className="exp-group-meta">
                      Total: {formatCurrency(group.total)} · Expenses: {group.expenses.length}
                      {hasNew && (
                        <span className="exp-group-new-label"> · {group.pendingCount} new</span>
                      )}
                    </span>
                  </div>
                </button>
                <div className={`exp-project-group-body ${isExpanded ? 'is-expanded' : ''}`}>
                  <div className="exp-group-table-wrap">
                    <table className="exp-group-table">
                      <thead>
                        <tr>
                          <th className="exp-col-check" />
                          <th>Expense</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Employee</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.expenses.map((exp) => (
                          <tr key={exp.id}>
                            <td className="exp-col-check">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(exp.id)}
                                onChange={() => toggleSelect(exp.id)}
                                aria-label={`Select expense ${exp.expense}`}
                              />
                            </td>
                            <td>
                              <span className="exp-name-cell">
                                {exp.expense}
                                {exp.adminEdited && (
                                  <span className="exp-edited-icon" title={formatEditTooltip(exp)}>
                                    <i className="fas fa-pencil-alt" />
                                  </span>
                                )}
                              </span>
                            </td>
                            <td>{exp.date}</td>
                            <td>{formatCurrency(exp.amount)}</td>
                            <td><Badge variant={exp.status === 'Paid' ? 'success' : 'warning'}>{exp.status}</Badge></td>
                            <td>{exp.employeeName || '—'}</td>
                            <td>
                              <div className="exp-actions">
                                {!exp.approved && isAdmin && (
                                  <button type="button" className="exp-icon-btn success" onClick={() => handleApprove(exp)} title="Approve">
                                    <i className="fas fa-check" />
                                  </button>
                                )}
                                <button type="button" className="exp-icon-btn" onClick={() => openEdit(exp)} title="Edit">
                                  <i className="fas fa-edit" />
                                </button>
                                <button type="button" className="exp-icon-btn danger" onClick={() => setDeleteTarget(exp)} title="Delete">
                                  <i className="fas fa-trash" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile card layout */}
                  <div className="exp-mobile-cards">
                    {group.expenses.map((exp) => (
                      <div key={exp.id} className="exp-mobile-card">
                        <div className="exp-mobile-card-top">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(exp.id)}
                            onChange={() => toggleSelect(exp.id)}
                          />
                          <strong>
                            {exp.expense}
                            {exp.adminEdited && (
                              <span className="exp-edited-icon" title={formatEditTooltip(exp)}>
                                <i className="fas fa-pencil-alt" />
                              </span>
                            )}
                          </strong>
                        </div>
                        <div className="exp-mobile-card-meta">
                          <span>{exp.date}</span>
                          <span>{formatCurrency(exp.amount)}</span>
                          <Badge variant={exp.status === 'Paid' ? 'success' : 'warning'}>{exp.status}</Badge>
                        </div>
                        <div className="exp-mobile-card-actions">
                          {!exp.approved && isAdmin && (
                            <button type="button" className="exp-icon-btn success" onClick={() => handleApprove(exp)} title="Approve">
                              <i className="fas fa-check" />
                            </button>
                          )}
                          <button type="button" className="exp-icon-btn" onClick={() => openEdit(exp)} title="Edit">
                            <i className="fas fa-edit" />
                          </button>
                          <button type="button" className="exp-icon-btn danger" onClick={() => setDeleteTarget(exp)} title="Delete">
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="Add Expense"
        size="lg"
        footer={(
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="button" className="ui-btn ui-btn-primary" onClick={handleSave} disabled={saving || noActive}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        )}
      >
        {formFields}
      </Modal>

      <Modal
        open={!!editModal}
        onClose={() => { setEditModal(null); setForm(EMPTY); }}
        title="Edit Expense"
        size="lg"
        footer={(
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => { setEditModal(null); setForm(EMPTY); }}>Cancel</button>
            <button type="button" className="ui-btn ui-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        )}
      >
        {formFields}
        {editModal?.editHistory?.length > 0 && (
          <div className="exp-edit-history">
            <h5>Edit History</h5>
            <ul>
              {editModal.editHistory.slice().reverse().map((entry, i) => (
                <li key={i}>
                  <strong>{entry.editedBy}</strong> — {new Date(entry.editedDate).toLocaleString()}
                  <ul>
                    {entry.changes.map((c, j) => (
                      <li key={j}>{c.field}: {String(c.originalValue)} → {String(c.updatedValue)}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
        confirmLabel="Delete"
      />

      <ConfirmModal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
        title="Bulk Delete Expenses"
        message={`Are you sure you want to delete ${selectedIds.size} selected expense(s)? This action cannot be undone.`}
        confirmLabel={`Delete ${selectedIds.size}`}
      />
    </div>
  );
}
